// =============================================================================
// T-459 — Bureau de Matthieu « hyper efficace » : les pièces ajoutées autour des cartes.
//   · PanneauTransmettre   « Transmettre à… » : Chloé ou Joey (présélectionné d'après l'étape du dossier, jamais imposé), motif, note facultative
//   · LigneApprendre       « À l'avenir, faire pareil pour cet expéditeur / ce type de mail ? » [Oui] [Non]
//   · TousLesMails         grille OHGrid : une ligne par mail, recherche, filtres, tri, actions groupées ; un clic ouvre la carte à côté
//   · MatthieuAppris       « Ce que Matthieu a appris » : les règles, combien de fois appliquées, retirer
// Aucune décision ici : tout part par les routes du serveur, qui garde les protections (leads, expéditeurs protégés) et ne supprime jamais rien.
// Charte : jetons var(--oh-*) uniquement (2 modes), boutons de la charte, lisible à 390 px.
// =============================================================================
import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Ban, Archive, Download, Eye, Inbox, Send, Undo2 } from "lucide-react";
import { toast } from "sonner";

import { OhInput, OhStatusNote } from "../../../components/ui/oh-charte";
import { OHGrid } from "../../../components/ui/oh-grid";
import Bouton from "./boutonBureau";
import { NATURES, groupeDe } from "./bureauOrdre";
import * as api from "./bureauAmoApi";

const muted = { color: "var(--oh-text-muted)", fontSize: 14, lineHeight: 1.6 };
export const NOMS_AGENTS = { chloe: "Chloé", joey: "Joey" };
export const ETATS = { a_faire: "À faire", fait: "Fait", de_cote: "De côté", indesirable: "Indésirable" };

// ── Transmettre à… ───────────────────────────────────────────────────────────────────────────────────────
export function PanneauTransmettre({ refs, motifs = [], occupe, onConfirmer, onFermer }) {
  const [pour, setPour] = useState(null);
  const [propose, setPropose] = useState(null);
  const [motif, setMotif] = useState("autre");
  const [note, setNote] = useState("");
  const premiere = refs[0];
  useEffect(() => {
    let vivant = true;
    if (refs.length === 1) {
      api.transmissionProposee(premiere).then((r) => { if (vivant && r?.propose) { setPropose(r.propose); setPour((p) => p || r.propose); } }).catch(() => {});
    }
    return () => { vivant = false; };
  }, [premiere, refs.length]);
  return (
    <div style={{ marginTop: 8, padding: "var(--oh-space-3)", border: "0.5px solid var(--oh-border)", borderRadius: "var(--oh-radius-control)", background: "var(--oh-bg-surface)" }}
      role="group" aria-label="Transmettre à Chloé ou à Joey" data-testid="bureau-simple-transmission">
      <p style={{ ...muted, margin: "0 0 8px" }}>
        À qui transmettre {refs.length > 1 ? `ces ${refs.length} mails` : "ce mail"} ? {propose ? `L'étape du dossier désigne ${NOMS_AGENTS[propose]}, mais tu choisis.` : "Le mail doit être rattaché à un dossier."}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {Object.entries(NOMS_AGENTS).map(([cle, nom]) => (
          <Bouton key={cle} type={pour === cle ? "secondaire" : "discret"} aria-pressed={pour === cle} data-testid={`bureau-simple-pour-${cle}`} style={{ height: 44 }}
            onClick={() => setPour(cle)}>{nom}{propose === cle ? " (proposé)" : ""}</Bouton>
        ))}
      </div>
      {motifs.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" style={{ marginTop: 8 }}>
          {motifs.map((m) => (
            <Bouton key={m.id} type={motif === m.id ? "secondaire" : "discret"} aria-pressed={motif === m.id} data-testid={`bureau-simple-motif-${m.id}`} style={{ height: 44 }}
              onClick={() => setMotif(m.id)}>{m.libelle}</Bouton>
          ))}
        </div>
      )}
      <div style={{ marginTop: 8 }}>
        <OhInput aria-label="Note pour la personne (facultative)" placeholder="Une note ? (facultatif)" value={note} maxLength={300} data-testid="bureau-simple-transmission-note"
          onChange={(e) => setNote(e.target.value)} />
      </div>
      <div className="flex flex-wrap items-center gap-2" style={{ marginTop: 8 }}>
        <Bouton type="secondaire" disabled={occupe || !pour} data-testid="bureau-simple-transmission-confirmer" style={{ height: 44 }}
          onClick={() => onConfirmer({ pour, motif, note: note.trim() })}>Transmettre{pour ? ` à ${NOMS_AGENTS[pour]}` : ""}</Bouton>
        <Bouton type="discret" data-testid="bureau-simple-transmission-annuler" style={{ height: 44 }} onClick={onFermer}>Annuler</Bouton>
      </div>
    </div>
  );
}

// ── Rattacher un groupe à un dossier ─────────────────────────────────────────────────────────────────────
export function PanneauRattacher({ nombre, occupe, onChoisir, onFermer }) {
  const [q, setQ] = useState("");
  const [candidats, setCandidats] = useState([]);
  return (
    <div style={{ padding: "var(--oh-space-3)", border: "0.5px solid var(--oh-border)", borderRadius: "var(--oh-radius-control)", background: "var(--oh-bg-surface)" }} role="group"
      aria-label="Rattacher à un dossier" data-testid="bureau-tous-rattacher">
      <p style={{ ...muted, margin: "0 0 8px" }}>Rattacher {nombre > 1 ? `ces ${nombre} mails` : "ce mail"} à quel dossier ?</p>
      <OhInput aria-label="Chercher un dossier (référence)" placeholder="Référence du dossier (2 caractères au moins)" value={q} data-testid="bureau-tous-rattacher-q"
        onChange={async (e) => {
          const v = e.target.value;
          setQ(v);
          if (v.trim().length < 2) { setCandidats([]); return; }
          try { setCandidats((await api.dossiersCandidats(v.trim())).items || []); } catch { setCandidats([]); }
        }} />
      {candidats.map((c) => (
        <div key={c.id} className="flex items-center justify-between gap-3" style={{ marginTop: 8 }}>
          <span style={{ color: "var(--oh-text)", fontSize: 14 }}>{c.reference}</span>
          <Bouton type="secondaire" disabled={occupe} data-testid={`bureau-tous-rattacher-${c.id}`} aria-label={`Rattacher au dossier ${c.reference}`} onClick={() => onChoisir(c.id)}>Rattacher</Bouton>
        </div>
      ))}
      <div style={{ marginTop: 8 }}><Bouton type="discret" data-testid="bureau-tous-rattacher-annuler" onClick={onFermer}>Annuler</Bouton></div>
    </div>
  );
}

// ── Apprendre ────────────────────────────────────────────────────────────────────────────────────────────
export function LigneApprendre({ propose, occupe, onOui, onNon }) {
  if (!propose) return null;
  return (
    <div role="status" data-testid="bureau-apprendre" style={{ padding: "var(--oh-space-3)", border: "0.5px solid var(--oh-accent)", borderRadius: "var(--oh-radius-card)", background: "var(--oh-accent-bg)" }}>
      <div className="flex flex-wrap items-center gap-2">
        <span style={{ flex: "1 1 240px", color: "var(--oh-accent-text)", fontSize: 15, lineHeight: 1.6 }} data-testid="bureau-apprendre-phrase">
          À l'avenir, faire pareil pour cet expéditeur{propose.libelleType ? " / ce type de mail" : ""} ? <span style={{ fontWeight: 500 }}>{propose.resume}</span>
        </span>
        <Bouton type="secondaire" disabled={occupe} data-testid="bureau-apprendre-oui" style={{ height: 44 }} onClick={() => onOui("expediteur")}>Oui, cet expéditeur</Bouton>
        {propose.libelleType && (
          <Bouton type="secondaire" disabled={occupe} data-testid="bureau-apprendre-oui-type" style={{ height: 44 }} onClick={() => onOui("type")}>Oui, ce type de mail</Bouton>
        )}
        <Bouton type="discret" disabled={occupe} data-testid="bureau-apprendre-non" style={{ height: 44 }} onClick={onNon}>Non</Bouton>
      </div>
    </div>
  );
}

/** Ce que le serveur a PROPOSÉ d'après une règle apprise, sur une carte : un clic = faire pareil (rien n'est automatique). */
export function PropositionApprise({ appris, occupe, onFaire, onIgnorer }) {
  if (!appris) return null;
  return (
    <div style={{ marginTop: 8, padding: "var(--oh-space-3)", border: "0.5px solid var(--oh-accent)", borderRadius: "var(--oh-radius-control)", background: "var(--oh-accent-bg)" }} data-testid="bureau-simple-appris">
      <p style={{ margin: 0, color: "var(--oh-accent-text)", fontSize: 15, lineHeight: 1.6 }} data-testid="bureau-simple-appris-libelle">Matthieu a appris : {appris.libelle}</p>
      <div className="flex flex-wrap items-center gap-2" style={{ marginTop: 8 }}>
        <Bouton type="secondaire" disabled={occupe} data-testid="bureau-simple-appris-faire" style={{ height: 44 }} onClick={onFaire}>Faire pareil</Bouton>
        <Bouton type="discret" disabled={occupe} data-testid="bureau-simple-appris-ignorer" style={{ height: 44 }} onClick={onIgnorer}>Pas cette fois</Bouton>
      </div>
    </div>
  );
}

// ── Tous les mails ───────────────────────────────────────────────────────────────────────────────────────
const formatJour = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";
};

export function lignesDe(toutes) {
  return (Array.isArray(toutes) ? toutes : []).map((c) => ({
    ...c, id: c.ref, date_tri: c.quand || c.date || "", natureCle: groupeDe(c), natureTitre: NATURES[groupeDe(c)]?.titre || "—",
    dossierRef: c.dossier?.reference || "", etatLibelle: ETATS[c.etat] || c.etat,
  }));
}

export function rechercheMail(l, q) {
  return [l.expediteur, l.objet, l.dossierRef, l.qui].some((v) => typeof v === "string" && v.toLowerCase().includes(q));
}

export function TousLesMails({ toutes, chargement, erreur, tronque, onOuvrir, onGroupe, onRecharger, occupe }) {
  const lignes = useMemo(() => lignesDe(toutes), [toutes]);
  const colonnes = useMemo(() => [
    { key: "date_tri", label: "Date", sortValue: (r) => r.date_tri, render: (v) => formatJour(v) },
    { key: "expediteur", label: "Expéditeur", filter: { type: "text", placeholder: "Expéditeur…" }, render: (v, r) => (<span style={{ overflowWrap: "anywhere" }}>{v || r.qui || "—"}</span>) },
    { key: "objet", label: "Objet", filter: { type: "text", placeholder: "Objet…" }, render: (v) => (<span style={{ overflowWrap: "anywhere" }}>{v || "—"}</span>) },
    { key: "dossierRef", label: "Dossier", filter: { type: "text", placeholder: "Dossier…" }, render: (v) => v || "—" },
    { key: "natureTitre", label: "Nature", filter: { type: "select", options: Object.values(NATURES).filter((n) => ["urgent", "rappeler", "reponses", "trancher", "rattacher", "transmis", "aclasser", "autres"].includes(n.cle)).map((n) => ({ value: n.titre, label: n.titre })) } },
    { key: "etatLibelle", label: "État", filter: { type: "select", options: Object.values(ETATS).map((e) => ({ value: e, label: e })) } },
  ], []);
  // Charte OHGrid : actions par ligne, tri par colonne, actions groupées, tri par défaut « le plus récent d'abord ». L'export CSV de l'ancienne grille « Fiches de Matthieu » est repris.
  const rowActions = [
    { label: "Ouvrir la carte", icon: Eye, onClick: (l) => onOuvrir(l) },
    { label: "Indésirable", icon: Ban, onClick: (l) => onGroupe("indesirable", [l.ref]) },
    { label: "Inutile, classer", icon: Archive, onClick: (l) => onGroupe("inutile", [l.ref]) },
    { label: "Mettre de côté", icon: Archive, onClick: (l) => onGroupe("de_cote", [l.ref]) },
  ];
  const exporter = (ids) => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = lignes.filter((l) => ids.includes(l.id)).map((l) => [l.date_tri, l.expediteur || l.qui, l.objet, l.dossierRef, l.natureTitre, l.etatLibelle].map(esc).join(";"));
    const contenu = [["Date", "Expéditeur", "Objet", "Dossier", "Nature", "État"].map(esc).join(";"), ...rows].join("\n");
    const url = URL.createObjectURL(new Blob([contenu], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url; a.download = "tous-les-mails-selection.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  const actions = [
    { label: (n) => `Indésirable (${n})`, icon: Ban, onClick: (ids) => onGroupe("indesirable", ids) },
    { label: (n) => `Inutile, classer (${n})`, icon: Archive, onClick: (ids) => onGroupe("inutile", ids) },
    { label: (n) => `Transmettre à… (${n})`, icon: Send, onClick: (ids) => onGroupe("transmettre", ids) },
    { label: (n) => `Rattacher à un dossier (${n})`, icon: Inbox, onClick: (ids) => onGroupe("rattacher", ids) },
    { label: (n) => `Mettre de côté (${n})`, icon: Archive, onClick: (ids) => onGroupe("de_cote", ids) },
    { label: (n) => `Remettre à faire (${n})`, icon: Undo2, onClick: (ids) => onGroupe("remettre", ids) },
    { label: (n) => `Exporter la sélection (${n})`, icon: Download, onClick: exporter },
  ];
  return (
    <div data-testid="bureau-tous" style={{ display: "grid", gap: "var(--oh-space-3)" }}>
      {erreur && <OhStatusNote variant="error" title="Chargement impossible">{erreur}</OhStatusNote>}
      <OHGrid
        testIdPrefix="bureau-tous-grille"
        data={lignes}
        columns={colonnes}
        loading={chargement}
        onRefresh={onRecharger}
        defaultSortKey="date_tri"
        defaultSortDir="desc"
        searchPlaceholder="Chercher un expéditeur, un objet, un dossier, un client…"
        searchFilter={rechercheMail}
        selectable
        bulkActions={actions}
        rowActions={rowActions}
        onRowClick={(l) => !occupe && onOuvrir(l)}
        emptyMessage="Aucun mail pour le moment"
        pagination
        pageSize={50}
      />
      {tronque && <p style={muted}>Seuls les 500 mails les plus récents sont affichés.</p>}
    </div>
  );
}

// ── Ce que Matthieu sait (T-486) ─────────────────────────────────────────────────────────────────────────
// UNE lecture (`/simple/savoir`) pour les deux écrans : l'onglet « Ce que Matthieu a appris » du Bureau (lecture claire) et « Réglages de Matthieu » (on peut corriger).
// `section` : "tout" | "connaissances" | "regles".
export function SavoirMatthieu({ modifiable = false, section = "tout" }) {
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [edition, setEdition] = useState(null);       // { id, texte }
  const charger = () => api.savoir().then((r) => { setDonnees(r); setErreur(null); }).catch(() => setErreur("Impossible de lire ce que Matthieu sait pour le moment."));
  useEffect(() => { charger(); }, []);                // eslint-disable-line react-hooks/exhaustive-deps
  const retirer = async (r) => {
    try { await api.retirerAppris(r.id); toast.success("Règle retirée. Matthieu ne la proposera plus."); await charger(); }
    catch (e) { toast.error(typeof e.detail === "string" ? e.detail : "Impossible de retirer la règle."); }
  };
  const enregistrer = async () => {
    try { await api.corrigerConnaissance(edition.id, edition.texte); toast.success("Réponse corrigée. Matthieu s'en servira."); setEdition(null); await charger(); }
    catch (e) { toast.error(typeof e.detail === "string" ? e.detail : "Impossible d'enregistrer la correction."); }
  };
  const fiches = donnees?.connaissances || [];
  const regles = donnees?.regles || [];
  const voirFiches = section !== "regles";
  const voirRegles = section !== "connaissances";
  const rien = donnees && (voirFiches ? fiches.length === 0 : true) && (voirRegles ? regles.length === 0 : true);
  return (
    <section aria-label="Ce que Matthieu sait" data-testid="bureau-appris" style={{ display: "grid", gap: "var(--oh-space-4)" }}>
      <div>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 500, color: "var(--oh-text)" }}>Ce que Matthieu sait</h2>
        <p style={{ ...muted, margin: "4px 0 0" }} data-testid="bureau-appris-phrase">
          Les réponses que tu as validées ou corrigées, les choix que tu as faits et les règles que tu lui as apprises : chacune avec sa date et d'où elle vient.
          {modifiable ? " Tu peux corriger une réponse ou retirer une règle ici." : " Pour corriger quelque chose, ouvre « Réglages de Matthieu »."}
          {" "}Rien ne part sans ton clic, et les expéditeurs protégés (clients, leads, administrations…) ne sont jamais concernés.
        </p>
        {donnees && <p style={{ ...muted, margin: "4px 0 0" }} data-testid="bureau-appris-totaux">{donnees.totaux.connaissances} réponse{donnees.totaux.connaissances > 1 ? "s" : ""} et décision{donnees.totaux.connaissances > 1 ? "s" : ""} · {donnees.totaux.regles} règle{donnees.totaux.regles > 1 ? "s" : ""}{donnees.tronque ? " (les 200 plus récentes sont affichées)" : ""}</p>}
      </div>
      {erreur && <OhStatusNote variant="error" title="Chargement impossible">{erreur}</OhStatusNote>}
      {rien && <p style={{ ...muted, color: "var(--oh-text)" }} data-testid="bureau-appris-vide">Matthieu n'a encore rien appris. Il apprend de tes validations, de tes choix et des règles que tu lui donnes (réponds « Oui » à « À l'avenir, faire pareil ? »).</p>}

      {voirFiches && fiches.length > 0 && (
        <div style={{ display: "grid", gap: "var(--oh-space-3)" }} data-testid="bureau-savoir-fiches">
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 500, color: "var(--oh-text)" }}>Réponses et décisions</h3>
          <ul style={{ padding: 0, margin: 0, display: "grid", gap: "var(--oh-space-3)" }}>
            {fiches.map((f) => (
              <li key={f.id} data-testid={`bureau-savoir-fiche-${f.id}`} style={{ listStyle: "none", padding: "var(--oh-space-4)", background: "var(--oh-bg-elevated)", borderRadius: "var(--oh-radius-card)", border: "0.5px solid var(--oh-border)" }}>
                <p style={{ margin: 0, color: "var(--oh-text)", fontSize: 15, fontWeight: 500, overflowWrap: "anywhere" }} data-testid="bureau-savoir-question">{f.question}</p>
                {edition?.id === f.id ? (
                  <div style={{ marginTop: 8 }}>
                    <textarea aria-label="Réponse de Matthieu" data-testid="bureau-savoir-texte" value={edition.texte} maxLength={4000} rows={5} onChange={(e) => setEdition({ id: f.id, texte: e.target.value })}
                      style={{ width: "100%", padding: "var(--oh-space-3)", borderRadius: "var(--oh-radius-control)", border: "1px solid var(--oh-border)", background: "var(--oh-bg-surface)", color: "var(--oh-text)", font: "inherit" }} />
                    <div className="flex flex-wrap gap-2" style={{ marginTop: 8 }}>
                      <Bouton type="secondaire" data-testid="bureau-savoir-enregistrer" disabled={!edition.texte.trim()} style={{ height: 44 }} onClick={enregistrer}>Enregistrer la correction</Bouton>
                      <Bouton type="discret" data-testid="bureau-savoir-annuler" style={{ height: 44 }} onClick={() => setEdition(null)}>Annuler</Bouton>
                    </div>
                  </div>
                ) : (
                  <p style={{ margin: "4px 0 0", color: "var(--oh-text)", fontSize: 14, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }} data-testid="bureau-savoir-reponse">{f.reponse}</p>
                )}
                <p style={{ ...muted, margin: "8px 0 0" }} data-testid="bureau-savoir-meta">
                  {f.type_libelle} · {f.origine}{f.par ? ` · par ${f.par}` : ""} · le {formatJour(f.date)} · utilisée {f.usages} fois{f.pour_un_seul_dossier ? " · pour un seul dossier" : ""}
                </p>
                {modifiable && edition?.id !== f.id && (
                  <div style={{ marginTop: 8 }}><Bouton type="discret" data-testid="bureau-savoir-corriger" aria-label={`Corriger la réponse : ${f.question}`} style={{ height: 44 }} onClick={() => setEdition({ id: f.id, texte: f.reponse || "" })}>Corriger la réponse</Bouton></div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {voirRegles && regles.length > 0 && (
        <div style={{ display: "grid", gap: "var(--oh-space-3)" }} data-testid="bureau-savoir-regles">
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 500, color: "var(--oh-text)" }}>Règles apprises de tes décisions</h3>
          <ul style={{ padding: 0, margin: 0, display: "grid", gap: "var(--oh-space-3)" }}>
            {regles.map((r) => (
              <li key={r.id} data-testid={`bureau-appris-regle-${r.id}`} style={{ listStyle: "none", padding: "var(--oh-space-4)", background: "var(--oh-bg-elevated)", borderRadius: "var(--oh-radius-card)", border: "0.5px solid var(--oh-border)" }}>
                <p style={{ margin: 0, color: "var(--oh-text)", fontSize: 15, overflowWrap: "anywhere" }} data-testid="bureau-appris-libelle">{r.libelle}</p>
                <p style={{ ...muted, margin: "4px 0 0" }} data-testid="bureau-appris-compteur">{r.origine} · le {formatJour(r.cree_le)} · appliquée {r.appliquee} fois{r.derniere_application ? ` · dernière fois le ${formatJour(r.derniere_application)}` : ""}</p>
                {modifiable && <div style={{ marginTop: 8 }}><Bouton type="discret" data-testid="bureau-appris-retirer" aria-label={`Retirer la règle : ${r.libelle}`} style={{ height: 44 }} onClick={() => retirer(r)}>Retirer cette règle</Bouton></div>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
export const MatthieuAppris = SavoirMatthieu;       // ancien nom (T-459), gardé pour les appelants existants

export function RetourListe({ onClick }) {
  return <Bouton type="discret" data-testid="bureau-tous-retour" onClick={onClick}><ArrowLeft className="w-4 h-4 inline mr-1" aria-hidden="true" />Retour à la liste</Bouton>;
}
